import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Session error:', error);
      } else {
        console.log('✅ Initial session loaded:', session?.user?.email || 'No user');
      }
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, session?.user?.email || 'No user');

      if (event === 'SIGNED_IN') {
        console.log('✅ User signed in successfully:', session?.user?.email);
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed');
      } else if (event === 'USER_UPDATED') {
        console.log('👤 User updated');
      } else if (event === 'PASSWORD_RECOVERY') {
        console.log('🔑 Password recovery event detected');
        // Redirect to reset password page if not already there
        if (!window.location.pathname.includes('/reset-password')) {
          console.log('→ Redirecting to reset-password page...');
          window.location.href = '/reset-password' + window.location.hash;
          return;
        }
      }

      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signUp: (data) => supabase.auth.signUp(data),
    signIn: (data) => supabase.auth.signInWithPassword(data),
    signInWithGoogle: async () => {
      console.log('🔑 Attempting Google OAuth sign in...');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error('❌ Google OAuth error:', error);
      } else {
        console.log('🔑 Google OAuth initiated, redirecting to Google...');
      }

      return { data, error };
    },
    signInWithFacebook: async () => {
      console.log('🔑 Attempting Facebook OAuth sign in...');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        console.error('❌ Facebook OAuth error:', error);
      } else {
        console.log('🔑 Facebook OAuth initiated, redirecting to Facebook...');
      }

      return { data, error };
    },
    signOut: () => supabase.auth.signOut(),
    // Land on /reset-password itself. With PKCE, supabase-js signs the user in
    // from the link's ?code= but reports SIGNED_IN, not PASSWORD_RECOVERY, so
    // /auth/callback can't tell a reset from a login and sent them to /profile.
    resetPassword: (email) => supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    }),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
